import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit, Palette } from 'lucide-react';
import api from '../api';
import { Breadcrumbs } from './Breadcrumbs';

const TYPE_LABELS = {
  item_type: { singular: 'Item Type', plural: 'Item Types' },
  status: { singular: 'Status', plural: 'Statuses' },
  priority: { singular: 'Priority', plural: 'Priorities' }
};

function MetadataManagement({ user }) {
  const navigate = useNavigate();
  const [metadata, setMetadata] = useState([]);
  const [activeType, setActiveType] = useState('item_type');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [newMetadata, setNewMetadata] = useState({ type: 'item_type', value: '', label: '', color: '#82d230', order: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadMetadata();
  }, [activeType]);

  const loadMetadata = async () => {
    setLoading(true);
    try {
      const data = await api.getMetadata(activeType);
      setMetadata(data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Failed to load metadata:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const nextOrder = metadata.length > 0 ? Math.max(...metadata.map(m => m.order)) + 1 : 1;
      await api.createMetadata({ ...newMetadata, order: nextOrder });
      await loadMetadata();
      setShowCreateModal(false);
      setNewMetadata({ type: activeType, value: '', label: '', color: '#82d230', order: 0 });
    } catch (error) {
      console.error('Failed to create metadata:', error);
      alert('Failed to create metadata. Please try again.');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateMetadata(editingItem._id, {
        label: editingItem.label,
        color: editingItem.color,
        order: editingItem.order
      });
      await loadMetadata();
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to update metadata:', error);
      alert('Failed to update metadata');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Are you sure you want to delete this ${TYPE_LABELS[activeType].singular.toLowerCase()}?`)) return;
    try {
      await api.deleteMetadata(id);
      await loadMetadata();
    } catch (error) {
      console.error('Failed to delete metadata:', error);
      alert('Failed to delete metadata');
    }
  };

  return (
    <div className="min-h-full">
      {/* Breadcrumbs */}
      <div className="px-6 lg:px-8 pt-6 pb-2">
        <Breadcrumbs items={[
          { label: 'Dashboard', path: '/' },
          { label: 'Metadata', path: '/metadata' },
        ]} />
      </div>

      <div className="px-6 lg:px-8 pt-4 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Metadata Management</h1>
            <p className="text-sm text-muted-foreground mt-1">Configure item types, statuses, and priorities</p>
          </div>
          <button
            onClick={() => { setNewMetadata({ ...newMetadata, type: activeType }); setShowCreateModal(true); }}
            className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold flex items-center gap-2 shadow-sm shadow-primary/20 btn-press transition-opacity"
            data-testid="add-new-button"
          >
            <Plus className="h-4 w-4" /> Add New
          </button>
        </div>
      </div>

      <div className="px-6 lg:px-8 pb-8">
        {/* Tabs */}
        <div className="flex gap-3 mb-8 border-b border-border">
          {['item_type', 'status', 'priority'].map((type) => (
            <button
              key={type}
              onClick={() => setActiveType(type)}
              className={`px-6 py-3 font-medium border-b-2 transition-all ${
                activeType === type
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
              }`}
              data-testid={`tab-${type}`}
            >
              {TYPE_LABELS[type].plural}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-28 bg-muted rounded-xl skeleton"></div>
            ))}
          </div>
        ) : metadata.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16 bg-card rounded-xl border border-border">
            <Palette className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No {TYPE_LABELS[activeType].plural} Yet</h3>
            <p className="text-muted-foreground mb-6">Create your first {TYPE_LABELS[activeType].singular.toLowerCase()} to get started</p>
            <button
              onClick={() => { setNewMetadata({ ...newMetadata, type: activeType }); setShowCreateModal(true); }}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> Add {TYPE_LABELS[activeType].singular}
            </button>
          </div>
        ) : (
          /* Metadata Cards */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {metadata.map((item) => (
              <div 
                key={item._id} 
                className="group relative bg-card border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
                data-testid={`metadata-card-${item._id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3 flex-1">
                    <div 
                      className="w-5 h-5 rounded-md flex-shrink-0 shadow-sm border border-border/50" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground truncate">{item.label}</h3>
                      <p className="text-xs text-muted-foreground mt-0.5">Value: <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{item.value}</code></p>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingItem(item)}
                      className="p-1.5 hover:bg-secondary/20 rounded-md transition-colors"
                      title="Edit"
                      data-testid={`edit-${item._id}`}
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(item._id)}
                      className="p-1.5 hover:bg-destructive/10 text-destructive rounded-md transition-colors"
                      title="Delete"
                      data-testid={`delete-${item._id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                  <span className="px-2 py-1 bg-muted rounded-md">Order: {item.order}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-modal">
          <div className="bg-card rounded-xl max-w-md w-full border border-border shadow-2xl">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-bold">Add New {TYPE_LABELS[activeType].singular}</h2>
              <p className="text-sm text-muted-foreground mt-1">Create a custom {TYPE_LABELS[activeType].singular.toLowerCase()} for your roadmap</p>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Value (internal identifier)</label>
                <input
                  type="text"
                  value={newMetadata.value}
                  onChange={(e) => setNewMetadata({ ...newMetadata, value: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                  required
                  className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                  placeholder="e.g., epic, bug-fix"
                  data-testid="value-input"
                />
                <p className="text-xs text-muted-foreground mt-1.5">Lowercase, no spaces (use hyphens)</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Label (display name)</label>
                <input
                  type="text"
                  value={newMetadata.label}
                  onChange={(e) => setNewMetadata({ ...newMetadata, label: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                  placeholder="e.g., Epic, Bug Fix"
                  data-testid="label-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={newMetadata.color}
                    onChange={(e) => setNewMetadata({ ...newMetadata, color: e.target.value })}
                    className="w-16 h-12 border border-input rounded-lg cursor-pointer"
                    data-testid="color-input"
                  />
                  <div className="flex-1 px-4 py-2.5 border border-input rounded-lg bg-muted">
                    <code className="text-sm font-mono">{newMetadata.color}</code>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowCreateModal(false)} 
                  className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 font-medium transition-all"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-all shadow-sm"
                  data-testid="create-submit"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="edit-modal">
          <div className="bg-card rounded-xl max-w-md w-full border border-border shadow-2xl">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-bold">Edit {TYPE_LABELS[activeType].singular}</h2>
            </div>
            <form onSubmit={handleUpdate} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Label</label>
                <input
                  type="text"
                  value={editingItem.label}
                  onChange={(e) => setEditingItem({ ...editingItem, label: e.target.value })}
                  required
                  className="w-full px-4 py-2.5 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="edit-label-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Color</label>
                <div className="flex gap-3 items-center">
                  <input
                    type="color"
                    value={editingItem.color}
                    onChange={(e) => setEditingItem({ ...editingItem, color: e.target.value })}
                    className="w-16 h-12 border border-input rounded-lg cursor-pointer"
                    data-testid="edit-color-input"
                  />
                  <div className="flex-1 px-4 py-2.5 border border-input rounded-lg bg-muted">
                    <code className="text-sm font-mono">{editingItem.color}</code>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button type="button" onClick={() => setEditingItem(null)} className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 font-medium">
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium shadow-sm" data-testid="update-submit">
                  Update
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MetadataManagement;
